// Printing (or "Save as PDF" in the print dialog). A browser and the desktop app print a hidden
// frame; the phone apps' web views cannot print themselves (window.print does nothing there and
// a window.open left the app black), so they hand it to the system's printing:
// android/.../PrintPlugin.java (PrintManager) and ios/App/App/PrintPlugin.swift
// (UIPrintInteractionController).
import { registerPlugin } from '@capacitor/core';
import { isNative } from './platform';

const NativePrint = registerPlugin('BusyBeePrint');

// A printed page that reads like a document, not a web page: page margins, a type scale,
// tight lists and tables, real checkboxes, nothing split across pages that should stay whole.
const PAGE_STYLE = `@page{margin:18mm 16mm}
*{box-sizing:border-box}
html{-webkit-print-color-adjust:exact;print-color-adjust:exact}
body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,'Helvetica Neue',Arial,'Noto Sans',sans-serif;font-size:11pt;line-height:1.55;color:#18181b;margin:0 auto;max-width:720px;padding:32px 24px;word-wrap:break-word}
@media print{body{max-width:none;padding:0}}
h1,h2,h3,h4,h5,h6{line-height:1.3;margin:1.3em 0 .45em;font-weight:650;break-after:avoid;page-break-after:avoid}
h1{font-size:18pt}h2{font-size:15pt}h3{font-size:12.5pt}h4,h5,h6{font-size:11pt}
h1.bb-title{font-size:24pt;font-weight:700;margin:0 0 14pt}
p{margin:0 0 .6em}
ul,ol{margin:0 0 .6em;padding-left:1.4em}
li{margin:.15em 0}li>p{margin:0}li>ul,li>ol{margin:.15em 0 0}
li.bb-check{list-style:none}
li.bb-check>input{float:left;margin:.32em 0 0 -1.3em;width:.95em;height:.95em}
li.bb-done>p{color:#71717a;text-decoration:line-through}
a{color:#b45309;text-decoration:underline}
code{font-family:ui-monospace,SFMono-Regular,Menlo,Consolas,monospace;font-size:.9em;background:#f4f4f5;padding:.1em .35em;border-radius:4px}
pre{background:#f4f4f5;padding:10pt 12pt;border-radius:6px;white-space:pre-wrap;word-break:break-word;font-size:9.5pt;line-height:1.45;margin:0 0 .8em;break-inside:avoid}
pre code{background:none;padding:0;font-size:inherit}
blockquote{border-left:3px solid #d4d4d8;margin:0 0 .8em;padding:.1em 0 .1em 12pt;color:#52525b}
blockquote p{margin:0}
table{border-collapse:collapse;width:100%;margin:0 0 .9em;font-size:10pt}
tr{break-inside:avoid}
th,td{border:1px solid #d4d4d8;padding:5pt 8pt;text-align:left;vertical-align:top}
th{background:#f4f4f5;font-weight:600}
td>p,th>p{margin:0}
img,video{max-width:100%;height:auto;break-inside:avoid}
figure{margin:0 0 .8em}
hr{border:0;border-top:1px solid #e4e4e7;margin:1.2em 0}
details>summary{list-style:none}`;

const escape = (s) => String(s || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

/** The editor's HTML made printable: checklists as checkboxes, no leftover empty lines or
 * object marks (U+FFFC, a stray "OBJ" box at the end of every printed page). */
export function printableBody(html) {
  if (!html || typeof DOMParser === 'undefined') return html || '';
  const doc = new DOMParser().parseFromString(`<body>${html.replace(/\uFFFC/g, '')}</body>`, 'text/html');
  const body = doc.body;
  // A to-do keeps its place in a list that mixes points and to-dos: its box stands where the
  // point would be.
  body.querySelectorAll('input[type="checkbox"]').forEach((box) => {
    const li = box.closest('li');
    if (!li) return;
    li.classList.add('bb-check');
    if (box.checked || box.hasAttribute('checked')) li.classList.add('bb-done');
    box.setAttribute('disabled', '');
    if (box.parentElement !== li) li.insertBefore(box, li.firstChild);
  });
  // Empty paragraphs at the end (the editor always keeps one to type into).
  let last = body.lastElementChild;
  while (last && last.tagName === 'P' && !last.textContent.trim() && !last.querySelector('img,video,iframe')) {
    last.remove();
    last = body.lastElementChild;
  }
  return body.innerHTML;
}

/** A printable page: the title as its heading, then the HTML. */
export const printablePage = (title, html) => `<!doctype html><html><head><meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1"><title>${escape(title)}</title>
<style>${PAGE_STYLE}</style></head><body><h1 class="bb-title">${escape(title)}</h1>${printableBody(html)}</body></html>`;

/** Prints a document: its title and HTML on their own page. */
export async function printHtml(title, html) {
  const page = printablePage(title, html);
  if (isNative()) {
    await NativePrint.print({ name: title || 'BusyBee.Day', html: page });
    return;
  }
  const frame = document.createElement('iframe');
  frame.setAttribute('aria-hidden', 'true');
  frame.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden';
  const remove = () => frame.parentNode && frame.parentNode.removeChild(frame);
  frame.onload = () => {
    const w = frame.contentWindow;
    w.addEventListener('afterprint', () => setTimeout(remove, 0));
    w.focus();
    w.print();
    setTimeout(remove, 60_000);  // Safari's print does not wait: leave it time
  };
  frame.srcdoc = page;
  document.body.appendChild(frame);
}

/** Prints what is on the screen (a task). */
export async function printPage() {
  if (isNative()) {
    await NativePrint.print({});
    return;
  }
  window.print();
}
